import PaginatedSelect, {
  SelectedValue,
} from "@/components/ui/PaginatedSelect";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { usePromptVersions } from "@/queries";
import { ListPromptVersion } from "@/types";
import { UseQueryResult } from "@tanstack/react-query";

interface PromptVersionSelectorProps {
  promptId: number;
  selectedVersion?: {
    id: string | number;
    number: string | number;
  } | null;
  onValueChange: (version: ListPromptVersion) => void;
  onRemove?: () => void;
  showLabel?: boolean;
}

const PromptVersionSelector = ({
  promptId,
  selectedVersion,
  onValueChange,
  onRemove,
  showLabel = true,
}: PromptVersionSelectorProps) => {
  const workspaceId = useUser()?.activeWorkspaceId!;
  const userToken = useAuth()?.userToken!;

  const Query = (page: number): UseQueryResult<any, unknown> => {
    const result = usePromptVersions(userToken || "", {
      promptRegistryId: promptId,
      workspaceId: workspaceId,
      page: page,
      perPage: 10,
    });

    return result;
  };

  const extractVersionsData = (data: any) => {
    if (!data) return { data: [], totalPages: 0 };

    const items = data.pages.flatMap((page: any) => page.prompt_versions);
    const totalPages = data.pages[0]?.pages || 0;

    return {
      data: items,
      totalPages,
    };
  };

  const handleVersionChange = (value: SelectedValue<ListPromptVersion>) => {
    onValueChange(value.item);
  };

  return (
    <div>
      {showLabel && (
        <label
          htmlFor="promptVersion"
          className="mb-1 block text-sm font-medium"
        >
          Prompt Version
        </label>
      )}
      <div className="flex flex-row items-center gap-x-2 overflow-visible">
        <PaginatedSelect<ListPromptVersion, any>
          query={Query}
          renderItem={(item) => (
            <div className="flex flex-col">
              <span className="font-medium">Version {item.number}</span>
              <span className="text-xs text-gray-500">
                {item.commit_message}
              </span>
              <div className="mt-1 flex flex-wrap gap-1">
                {Array.isArray(item.labels) &&
                  item.labels.map((label, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center rounded border bg-white px-2 py-0.5 text-xs font-medium"
                    >
                      {label.name}
                    </span>
                  ))}
              </div>
            </div>
          )}
          getItemValue={(item) => item.number.toString()}
          value={
            selectedVersion
              ? {
                  id: selectedVersion.number.toString(),
                  item: {
                    id: selectedVersion.id.toString(),
                    number: selectedVersion.number,
                    name: `Version ${selectedVersion.number}`,
                  } as any,
                }
              : undefined
          }
          onValueChange={handleVersionChange}
          onRemove={onRemove}
          placeholder="Select a version..."
          extractData={extractVersionsData}
        />
      </div>
    </div>
  );
};

export default PromptVersionSelector;
